
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, AlertCircle } from "lucide-react";
import { useState } from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { TestResultDisplay } from "./TestResultDisplay";
import { supabase } from "@/integrations/supabase/client";

const telegramPostSchema = z.object({
  channel: z.string().min(1, { message: "Telegram channel is required" }),
  postId: z.string().min(1, { message: "Message ID is required" }),
});

const telegramLinkTestSchema = z.object({
  postLink: z.string().min(1, { message: "Telegram post link is required" })
    .url({ message: "Please enter a valid URL" })
    .refine(val => val.includes("t.me/"), {
      message: "Must be a valid Telegram link (t.me/...)"
    }),
});

interface TelegramSettingsProps {
  form: UseFormReturn<{
    telegramApiId?: string;
    telegramApiHash?: string;
    telegramSession?: string;
    [key: string]: string | undefined;
  }>;
}

export const TelegramSettings = ({ form }: TelegramSettingsProps) => {
  const { toast } = useToast();
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [trackingTelegramPost, setTrackingTelegramPost] = useState(false);
  const [testingTelegramLink, setTestingTelegramLink] = useState(false);
  const [savingTelegramSession, setSavingTelegramSession] = useState(false);
  const [telegramStatus, setTelegramStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [telegramSessionStatus, setTelegramSessionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [telegramTestResult, setTelegramTestResult] = useState<any>(null);
  const [telegramError, setTelegramError] = useState<string | null>(null);
  const [showRawData, setShowRawData] = useState(false);

  const telegramForm = useForm<z.infer<typeof telegramPostSchema>>({
    resolver: zodResolver(telegramPostSchema),
    defaultValues: {
      channel: "",
      postId: "",
    },
  });

  const telegramLinkForm = useForm<z.infer<typeof telegramLinkTestSchema>>({
    resolver: zodResolver(telegramLinkTestSchema),
    defaultValues: {
      postLink: "",
    },
  });

  const testTelegramApi = async () => {
    const apiId = form.getValues("telegramApiId");
    const apiHash = form.getValues("telegramApiHash");
    const sessionString = form.getValues("telegramSession");
    
    if (!apiId || !apiHash) {
      toast({
        title: "API Credentials Required",
        description: "Please enter both Telegram API ID and API Hash before testing.",
        variant: "destructive",
      });
      return;
    }
    
    setTestingTelegram(true);
    
    try {
      console.log("Saving Telegram API credentials and session...");
      const currentTimeStr = new Date().toISOString();
      
      // Save API ID
      const { data: existingApiId, error: apiIdQueryError } = await supabase
        .from('integration_settings')
        .select('*')
        .eq('integration_name', 'telegram_api_id')
        .maybeSingle();
      
      if (apiIdQueryError) throw new Error(`Failed to check existing API ID: ${apiIdQueryError.message}`);
      
      if (existingApiId) {
        const { error } = await supabase
          .from('integration_settings')
          .update({
            api_key: apiId,
            updated_at: currentTimeStr
          })
          .eq('integration_name', 'telegram_api_id');
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('integration_settings')
          .insert({
            integration_name: 'telegram_api_id',
            api_key: apiId,
            updated_at: currentTimeStr
          });
        if (error) throw error;
      }
      
      // Save API Hash with similar logic
      const { data: existingApiHash, error: apiHashQueryError } = await supabase
        .from('integration_settings')
        .select('*')
        .eq('integration_name', 'telegram_api_hash')
        .maybeSingle();
      
      if (apiHashQueryError) {
        console.error("Error checking existing API Hash:", apiHashQueryError);
        throw new Error(`Failed to check existing API Hash: ${apiHashQueryError.message}`);
      }
      
      let apiHashResult;
      if (existingApiHash) {
        console.log("Updating existing API Hash record");
        apiHashResult = await supabase
          .from('integration_settings')
          .update({
            api_key: apiHash,
            updated_at: currentTimeStr
          })
          .eq('integration_name', 'telegram_api_hash');
      } else {
        console.log("Creating new API Hash record");
        apiHashResult = await supabase
          .from('integration_settings')
          .insert({
            integration_name: 'telegram_api_hash',
            api_key: apiHash,
            updated_at: currentTimeStr
          });
      }
      
      if (apiHashResult.error) {
        throw new Error(`Failed to save API Hash: ${apiHashResult.error.message}`);
      }
      
      // Save Session String if provided
      if (sessionString) {
        const { data: existingSession, error: sessionQueryError } = await supabase
          .from('integration_settings')
          .select('*')
          .eq('integration_name', 'telegram_session')
          .maybeSingle();
        
        if (sessionQueryError) {
          console.error("Error checking existing session:", sessionQueryError);
          throw new Error(`Failed to check existing session: ${sessionQueryError.message}`);
        }
        
        console.log("Existing session check complete:", existingSession ? "Found existing" : "No existing session");
        
        let sessionResult;
        if (existingSession) {
          console.log("Updating existing session string");
          sessionResult = await supabase
            .from('integration_settings')
            .update({
              api_key: sessionString,
              updated_at: currentTimeStr
            })
            .eq('integration_name', 'telegram_session');
        } else {
          console.log("Creating new session string record");
          sessionResult = await supabase
            .from('integration_settings')
            .insert({
              integration_name: 'telegram_session',
              api_key: sessionString,
              updated_at: currentTimeStr
            });
        }
        
        if (sessionResult.error) {
          console.error("Error saving session:", sessionResult.error);
          throw new Error(`Failed to save session string: ${sessionResult.error.message}`);
        }
        
        setTelegramSessionStatus('success');
      }
      
      setTelegramStatus('success');
      toast({
        title: "Telegram API Settings Saved",
        description: sessionString 
          ? "Your Telegram API credentials and session string have been saved." 
          : "Your Telegram API credentials have been saved.",
      });
      
    } catch (error: any) {
      console.error("Error saving Telegram settings:", error);
      setTelegramStatus('error');
      toast({
        title: "Telegram API Test Failed",
        description: error.message || "Unable to save Telegram settings.",
        variant: "destructive",
      });
    } finally {
      setTestingTelegram(false);
    }
  };

  const trackTelegramPost = async (data: z.infer<typeof telegramPostSchema>) => {
    setTrackingTelegramPost(true);
    setTelegramTestResult(null);
    
    try {
      const { data: result, error } = await supabase.functions.invoke('telegram-track-views', {
        body: {
          channel: data.channel,
          postId: data.postId
        }
      });
      
      if (error) throw error;
      
      if (result.status === 'validated') {
        toast({
          title: "Telegram Link Validated",
          description: result.message || "The link was validated, but actual view count is not available.",
          variant: "default",
        });
      } else {
        toast({
          title: "Telegram Post Link Processed",
          description: result.message || `Processed post data with ${result.views} views.`,
          variant: "default",
        });
      }
      
      setTelegramTestResult(result);
      telegramForm.reset();
      
    } catch (error: any) {
      console.error("Error tracking Telegram post views:", error);
      const errorMessage = error.message || "An error occurred while processing the post data.";
      
      toast({
        title: "Error Processing Post",
        description: errorMessage,
        variant: "destructive",
      });
      
      setTelegramError(errorMessage);
    } finally {
      setTrackingTelegramPost(false);
    }
  };

  const testTelegramLink = async (data: z.infer<typeof telegramLinkTestSchema>) => {
    setTestingTelegramLink(true);
    setTelegramTestResult(null);
    setTelegramError(null);
    
    try {
      const { data: result, error } = await supabase.functions.invoke('telegram-track-views', {
        body: {
          postLink: data.postLink
        }
      });
      
      if (error) throw error;
      
      if (result.error) {
        setTelegramError(result.details || result.error);
        toast({
          title: "Telegram API Error",
          description: result.details || result.error,
          variant: "destructive",
        });
      } else {
        setTelegramTestResult(result);
        
        if (result.status === 'validated') {
          toast({
            title: "Telegram Link Validated",
            description: "The link format is valid, but actual view count is not available.",
            variant: "default",
          });
        } else {
          toast({
            title: "Telegram Post Link Processed",
            description: result.message || `Processed post data with ${result.views} views.`,
            variant: "default",
          });
        }
      }
    } catch (error: any) {
      console.error("Error testing Telegram post link:", error);
      const errorMessage = error.message || "An error occurred while processing the Telegram post link.";
      
      setTelegramError(errorMessage);
      toast({
        title: "Error Processing Link",
        description: errorMessage,
        variant: "destructive",
      });
      
      setTelegramTestResult(null);
    } finally {
      setTestingTelegramLink(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Telegram API</CardTitle>
        <CardDescription>Configure your Telegram API integration for tracking post views.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <Form {...form}>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="telegramApiId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telegram API ID</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter your Telegram API ID" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="telegramApiHash"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telegram API Hash</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" placeholder="Enter your Telegram API Hash" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="telegramSession"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telegram Session String</FormLabel>
                    <FormControl>
                      <Textarea 
                        {...field} 
                        placeholder="Enter your Telegram session string" 
                        rows={4}
                      />
                    </FormControl>
                    <FormDescription>
                      This can be generated using Telegram's authentication flow.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Form>
          
          <div className="flex space-x-2">
            <Button 
              variant="default" 
              onClick={testTelegramApi} 
              disabled={testingTelegram}
            >
              {testingTelegram ? "Testing..." : "Save Telegram Settings"}
              {telegramStatus === 'success' && <CheckCircle className="ml-2 h-4 w-4 text-green-500" />}
              {telegramStatus === 'error' && <AlertCircle className="ml-2 h-4 w-4 text-red-500" />}
            </Button>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col items-start space-y-4">
        <div className="w-full">
          <h3 className="text-sm font-medium mb-2">Test Telegram Post Tracking</h3>
          <div className="space-y-4">
            <div className="border border-border rounded-md p-4">
              <h4 className="text-sm font-medium mb-2">Track by Channel & Post ID</h4>
              <Form {...telegramForm}>
                <form onSubmit={telegramForm.handleSubmit(trackTelegramPost)} className="space-y-4">
                  <FormField
                    control={telegramForm.control}
                    name="channel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Channel Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g., telegram_channel_name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={telegramForm.control}
                    name="postId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Post ID</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g., 123" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Button type="submit" disabled={trackingTelegramPost}>
                    {trackingTelegramPost ? "Processing..." : "Track Post Views"}
                  </Button>
                </form>
              </Form>
            </div>
            
            <div className="border border-border rounded-md p-4">
              <h4 className="text-sm font-medium mb-2">Track by Direct Link</h4>
              <Form {...telegramLinkForm}>
                <form onSubmit={telegramLinkForm.handleSubmit(testTelegramLink)} className="space-y-4">
                  <FormField
                    control={telegramLinkForm.control}
                    name="postLink"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Post Link</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g., https://t.me/channelname/123" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Button type="submit" disabled={testingTelegramLink}>
                    {testingTelegramLink ? "Processing..." : "Test Link"}
                  </Button>
                </form>
              </Form>
            </div>
          </div>
          
          <TestResultDisplay
            result={telegramTestResult}
            error={telegramError}
            showRawData={showRawData}
            onToggleRawData={() => setShowRawData(!showRawData)}
            title="Telegram API Response"
          />
        </div>
      </CardFooter>
    </Card>
  );
};
